"use client"

import { useBlockchain } from "@/app/blockchains/use-blockchain"
import type {
  Blockchain,
  EthereumBlockchainConfig,
} from "@anaconda/core/blockchain/types"
import {
  type CartPaymentSupport,
  readCartPaymentSupport,
} from "@rarible_int/anaconda_sdk/ethereum"
import { useQuery } from "@tanstack/react-query"
import { http, type Address, createPublicClient } from "viem"

/**
 * What the chain's cart contract (exchange wrapper) can be paid in, read
 * on-chain; undefined while unknown or on a chain without one.
 */
export function useCartPaymentSupport(
  blockchain: Blockchain,
): CartPaymentSupport | undefined {
  const config = useBlockchain(blockchain)
  const evm = config.isEvm ? (config as EthereumBlockchainConfig) : undefined
  const wrapper = evm?.exchange.wrapper as Address | undefined
  const rpcUrl =
    evm?.rpcUrls.find((url) => !url.isPublic)?.url ?? evm?.rpcUrls[0]?.url

  const { data } = useQuery({
    queryKey: ["cartPaymentSupport", blockchain, wrapper],
    enabled: Boolean(evm && wrapper && rpcUrl),
    staleTime: 5 * 60 * 1000,
    queryFn: () =>
      readCartPaymentSupport(
        createPublicClient({ chain: evm?.viem, transport: http(rpcUrl) }),
        wrapper as Address,
      ),
  })
  return data
}
